'use client';

import { Info } from '@1debit/icons';
import { useId, useState } from 'react';
import type { FocusEventHandler, InputHTMLAttributes, ReactNode, Ref } from 'react';
import { cn } from 'utils/styles';
import { HINT_TOGGLE_LABEL_PREFIX } from './constants';

export interface CalculatorInputAffix {
  symbol: '$' | '%';
  position: 'prefix' | 'suffix';
}

interface CalculatorInputProps extends InputHTMLAttributes<HTMLInputElement> {
  ref?: Ref<HTMLInputElement>;
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  invalid?: boolean;
  /** A unit marker held inside the field; replaces the focus-only `$`. */
  affix?: CalculatorInputAffix;
  /** Puts the hint behind an info toggle instead of showing it under the field. */
  collapsibleHint?: boolean;
}

const FOCUS_AFFIX: CalculatorInputAffix = { symbol: '$', position: 'prefix' };

/** Chimekit font sizes are stripped by tailwind-merge when they meet a color class in one `cn()`. */
const HINT_VISIBLE = 'mt-2 text-body-xs text-content-secondary';
const HINT_COLLAPSED = 'sr-only';

export const CalculatorInput = ({
  ref,
  id,
  label,
  hint,
  error,
  invalid = false,
  affix,
  collapsibleHint = false,
  className,
  placeholder = affix ? '0.00' : '$0.00',
  onFocus,
  onBlur,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  ...inputProps
}: CalculatorInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHintExpanded, setIsHintExpanded] = useState(false);
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const hasError = invalid && Boolean(error);
  // A toggle with nothing to control would leave `aria-controls` dangling.
  const hasHintToggle = collapsibleHint && Boolean(hint);
  const shownAffix = affix ?? (isFocused ? FOCUS_AFFIX : undefined);
  const describedBy =
    [ariaDescribedBy, hasError ? errorId : undefined, hint ? hintId : undefined]
      .filter(value => value !== undefined)
      .join(' ') || undefined;
  const handleFocus: FocusEventHandler<HTMLInputElement> = event => {
    setIsFocused(true);
    onFocus?.(event);
  };
  const handleBlur: FocusEventHandler<HTMLInputElement> = event => {
    setIsFocused(false);
    onBlur?.(event);
  };

  const labelElement = (
    <label htmlFor={inputId} className="block text-body-saans">
      {label}
    </label>
  );

  return (
    <div className="w-full">
      {hasHintToggle ? (
        <div className="flex items-center gap-2">
          {labelElement}
          <button
            // Submits the calculator form without this, since it sits inside one.
            type="button"
            onClick={() => setIsHintExpanded(current => !current)}
            aria-expanded={isHintExpanded}
            aria-controls={hintId}
            aria-label={`${HINT_TOGGLE_LABEL_PREFIX} ${label}`}
            className="cursor-pointer text-content-secondary">
            <Info aria-hidden className="size-[18px]" />
          </button>
        </div>
      ) : (
        labelElement
      )}
      <div className="relative mt-3">
        <input
          {...inputProps}
          ref={ref}
          id={inputId}
          placeholder={placeholder}
          aria-describedby={describedBy}
          aria-invalid={invalid || ariaInvalid || undefined}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn(
            'h-16 w-full rounded-lg border border-content-secondary bg-bg-primary px-6 text-body-saans text-content-primary outline-none transition-shadow',
            'placeholder:text-content-secondary hover:ring-[6px] hover:ring-bg-tertiary focus:border-content-primary focus:pl-10 focus:placeholder:text-transparent focus:ring-[6px] focus:ring-bg-tertiary',
            'disabled:cursor-not-allowed disabled:bg-accent-grey-100 disabled:text-content-disabled-primary disabled:hover:ring-transparent',
            affix?.position === 'prefix' && 'pl-10',
            // The focus gutter exists for the focus-only `$`; a suffix needs the room on the right.
            affix?.position === 'suffix' && 'pr-10 focus:pl-6',
            invalid &&
              'border-util-alert-500 bg-util-alert-100 hover:ring-util-alert-200 focus:border-util-alert-500 focus:ring-util-alert-200',
            className,
          )}
        />
        {shownAffix && (
          <span
            aria-hidden
            className={
              shownAffix.position === 'prefix'
                ? 'pointer-events-none absolute top-1/2 left-6 -translate-y-1/2 text-body-saans'
                : 'pointer-events-none absolute top-1/2 right-6 -translate-y-1/2 text-body-saans'
            }>
            {shownAffix.symbol}
          </span>
        )}
      </div>
      {hasError && (
        <p id={errorId} role="alert" aria-live="assertive" className="mt-3 text-body-saans text-util-alert-500">
          {error}
        </p>
      )}
      {hint && (
        // Kept in the DOM while collapsed so `aria-describedby` resolves whether or not it is expanded.
        <p id={hintId} className={hasHintToggle && !isHintExpanded ? HINT_COLLAPSED : HINT_VISIBLE}>
          {hint}
        </p>
      )}
    </div>
  );
};
